import React, { Component } from 'react';
import ReactSelect from 'react-select';
import classNames from 'classnames';

import style from './SelectField.module.scss';
import RootStore from '../../../../stores/RootStore';
import { SelectStylesProp } from './types';

export interface Option<T> {
  value: T;
  label: string | JSX.Element;
  disabled?: boolean;
}

export interface SelectFieldProps<T> {
  className?: string;
  invalid?: boolean;
  value: T | T[];
  onChange: (v: T | null, option: Option<T> | null) => void;
  options: Array<Option<T>>;
  disabled?: boolean;
  placeholder?: string;
  components?: any;
  isMulti?: boolean;
  closeMenuOnSelect?: boolean;
  menuIsOpen?: boolean;
  isLoading?: boolean;
}

class SelectField<T = any> extends Component<SelectFieldProps<T>> {
  render() {
    const {
      value,
      onChange,
      disabled,
      className,
      invalid,
      components,
      placeholder,
      closeMenuOnSelect,
      isMulti,
      ...props
    } = this.props;

    return (
      <ReactSelect
        {...props}
        isMulti={isMulti}
        closeMenuOnSelect={closeMenuOnSelect ?? !isMulti}
        menuPlacement="auto"
        menuPortalTarget={document.body}
        closeMenuOnScroll={this._closeMenuOnScroll}
        classNamePrefix="select"
        className={classNames(className, style.select, {
          [style.select__invalid]: invalid,
        })}
        styles={this._styles}
        isOptionDisabled={this._isOptionDisabled}
        isDisabled={disabled}
        onChange={this._handleChange}
        components={{ IndicatorSeparator: () => null, ...components }}
        value={this._value}
        noOptionsMessage={() =>
          RootStore.localization.formatMessage('select.noOptions')
        }
        placeholder={
          placeholder ||
          RootStore.localization.formatMessage('select.placeholder')
        }
      />
    );
  }

  private _handleChange = (
    selectedOption: Option<T> | Array<Option<T>> | null,
  ) => {
    if (Array.isArray(selectedOption)) {
      // Handle multi-select case
      const values = selectedOption.map(option => option.value);
      this.props.onChange(values as any, selectedOption as any);
    } else {
      // Handle single-select case
      this.props.onChange(
        selectedOption ? selectedOption.value : null,
        selectedOption || null,
      );
    }
  };

  private _isOptionDisabled = (option: Option<T>) => !!option.disabled;

  private _closeMenuOnScroll = (event: any) => {
    const className = event.target?.className;

    if (!className || className?.includes('menu-list')) {
      return false;
    }

    return true;
  };

  private get _value() {
    const { value, options, isMulti } = this.props;

    if (isMulti && Array.isArray(value)) {
      return options.filter(option => (value as T[]).includes(option.value));
    }

    return options.find(option => option.value === value) ?? null;
  }

  private get _styles(): SelectStylesProp {
    return {
      menuPortal: baseStyles => ({
        ...baseStyles,
        zIndex: 9999,
      }),
      menuList: baseStyles => ({
        ...baseStyles,
        color: 'var(--color-base)',
        fontSize: '14px',
      }),
      option: (baseStyles, state) => {
        let backgroundColor = 'transparent';
        let color = 'inherit';

        if (state.isFocused) {
          backgroundColor = '#f7f7f7';
        }
        if (state.isSelected) {
          backgroundColor = '#e6e6ec';
          color = '#333333';
        }

        return {
          ...baseStyles,
          backgroundColor,
          color,
          fontSize: 'inherit',
          cursor: 'pointer',
          ':active': {
            backgroundColor,
          },
        };
      },
    };
  }
}

export default SelectField;
